import { useRef, useState, type FormEvent } from "react";
import type {
  ContextRulePatchBody,
  ContextRuleView,
  RecipeMeResponse,
} from "@bitacorapro/api-types";
import { ApiError } from "../lib/api/error";
import { usePatchContextRule } from "../lib/api/queries";
import { Drawer } from "./Drawer";

// Edit one extracted rule (ADR-0173 fase 2, PR-7). Every save is a new
// immutable version on the server (v+1); the extractor's quote stays
// read-only underneath so the reviewer edits against the source, not from
// memory. Item codes are the client's own (`1.1`), validated server-side
// against the brand's active templates.

type Props = {
  rule: ContextRuleView;
  recipe: RecipeMeResponse | undefined;
  onClose: () => void;
  onSaved: (rule: ContextRuleView) => void;
};

const VERIFICATION_LABEL = { foto: "Foto", dato: "Dato", humano: "Humano" } as const;

export function responsibleOptions(recipe: RecipeMeResponse | undefined): string[] {
  const r = (recipe as { ruleResponsibles?: string[] } | undefined)?.ruleResponsibles ?? [];
  return [...r, "no_especificado"];
}

export function ContextRuleEditDrawer({ rule, recipe, onClose, onSaved }: Props) {
  const v = rule.version;
  const [text, setText] = useState(v.text);
  const [codes, setCodes] = useState(rule.itemCodes.join(", "));
  const [responsible, setResponsible] = useState(v.responsible);
  const [verification, setVerification] = useState<"foto" | "dato" | "humano">(v.verification);
  const [stock, setStock] = useState(v.stockConditioned);
  const [error, setError] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const patch = usePatchContextRule();

  const parsedCodes = codes
    .split(/[,\s]+/)
    .map((c) => c.trim())
    .filter(Boolean);
  const dirty =
    text.trim() !== v.text ||
    parsedCodes.join(",") !== rule.itemCodes.join(",") ||
    responsible !== v.responsible ||
    verification !== v.verification ||
    stock !== v.stockConditioned;
  const canSave = dirty && text.trim().length > 0 && text.length <= 600 && !patch.isPending;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    const body: ContextRulePatchBody = {
      ...(text.trim() !== v.text ? { text: text.trim() } : {}),
      ...(parsedCodes.join(",") !== rule.itemCodes.join(",") ? { itemCodes: parsedCodes } : {}),
      ...(responsible !== v.responsible ? { responsible } : {}),
      ...(verification !== v.verification ? { verification } : {}),
      ...(stock !== v.stockConditioned ? { stockConditioned: stock } : {}),
    };
    setError(null);
    try {
      onSaved(await patch.mutateAsync({ id: rule.id, body }));
    } catch (err) {
      if (err instanceof ApiError && err.code === "CONTEXT_ITEM_CODE_UNKNOWN") {
        const unknown = ((err.details ?? {}) as { unknown?: string[] }).unknown ?? [];
        setError(`Estos códigos no existen en el checklist de la marca: ${unknown.join(", ")}.`);
      } else {
        setError(err instanceof Error ? err.message : "No se pudo guardar.");
      }
    }
  }

  return (
    <Drawer
      title={`Editar regla · v${v.version}`}
      onClose={onClose}
      initialFocusRef={textRef}
      lead={
        v.sourceQuote ? (
          <blockquote className="cell-meta">
            <strong>Cita del documento:</strong> «{v.sourceQuote}»
          </blockquote>
        ) : undefined
      }
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="submit"
            form="context-rule-edit-form"
            className="btn btn--primary"
            disabled={!canSave}
          >
            {patch.isPending ? "Guardando…" : "Guardar como nueva versión"}
          </button>
        </>
      }
    >
      <form id="context-rule-edit-form" onSubmit={(e) => void submit(e)} className="stack-gap">
        <label className="field">
          <span className="field__label">Regla (≤600 caracteres)</span>
          <textarea
            ref={textRef}
            className="textarea"
            rows={4}
            maxLength={600}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <span className="field__hint">{text.length}/600</span>
        </label>
        <label className="field">
          <span className="field__label">Ítems del checklist (códigos, separados por coma)</span>
          <input
            className="input"
            value={codes}
            onChange={(e) => setCodes(e.target.value)}
            placeholder="1.1, 1.2"
          />
        </label>
        <label className="field">
          <span className="field__label">Responsable</span>
          <select
            className="select"
            value={responsible}
            onChange={(e) => setResponsible(e.target.value)}
          >
            {responsibleOptions(recipe).map((r) => (
              <option key={r} value={r}>
                {r === "no_especificado" ? "No especificado" : r}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">Cómo se verifica</span>
          <select
            className="select"
            value={verification}
            onChange={(e) => setVerification(e.target.value as "foto" | "dato" | "humano")}
          >
            {(["foto", "dato", "humano"] as const).map((k) => (
              <option key={k} value={k}>
                {VERIFICATION_LABEL[k]}
              </option>
            ))}
          </select>
          <span className="field__hint">
            Solo las reglas verificables por foto llegan al modelo; dato y humano quedan como
            recordatorio para la persona.
          </span>
        </label>
        <label
          className="field"
          style={{ flexDirection: "row", alignItems: "center", gap: "var(--s-2)" }}
        >
          <input type="checkbox" checked={stock} onChange={(e) => setStock(e.target.checked)} />
          <span className="field__label">Condicionada a stock</span>
        </label>
        {error && (
          <div className="empty" role="alert">
            <div className="empty__title">No se pudo guardar</div>
            <div className="empty__sub">{error}</div>
          </div>
        )}
      </form>
    </Drawer>
  );
}
